import { getMonthlySale, exportMonthlySale } from "apis/transaction.api";
import { DataTable, Icon } from "components/commons";
import { VenueContext, AppContext } from "contexts";
import { useApi, useFilter } from "hooks";
import { monthlySaleReportRequest, monthlySaleReportExportRequest } from "mappers/monthly-sale-report.mapper";
import React, { useCallback, useContext, useState, useMemo } from "react";
import { mapObject, pluralize } from "services";
import { HeaderA } from "components/headers";
import { monthlySaleReportFilterState } from "./filters";
import MonthlySaleReportFilter from "./monthly-sale-report-filter";
import { MonthlySaleReportColumns } from "./columns";
import lang from "translations";
import { ModuleWrapper } from "components/fragments";

const MonthlySaleReport = () => {
  const { venue } = useContext(VenueContext);
  const { setGlobalLoading } = useContext(AppContext);
  const {
    request: getMonthlySaleRequest,
    loading: loadingMonthlySaleReports,
    result: searchMonthlySaleReportResult = {
      data: { data: [] },
      metadata: { total: 0 }
    },
    error: errorMonthlySaleReports,
  } = useApi({
    api: getMonthlySale,
    isArray: true,
  });


  const {
    request: getMonthlySaleRequestExport,
    loading: getMonthlySaleRequestExportLoading,
  } = useApi({
    api: exportMonthlySale,
  });


  const { modifyFilter, modifyFilters, filterState, requestState, clearFilter, isFilterDirty } = useFilter(
    monthlySaleReportFilterState(venue)
  );

  const [hasAppliedFilter, setHasAppliedFilter] = useState(isFilterDirty);

  // useMount(() => {
  //   fetchMonthlySaleReports(filterState, requestState);
  // });

  const fetchMonthlySaleReports = useCallback(
    (filterState, requestState) => {
      setGlobalLoading(true);
      var dataToPass = mapObject({ ...requestState }, monthlySaleReportRequest);
      getMonthlySaleRequest(dataToPass);
      setGlobalLoading(false);
    },
    [getMonthlySaleRequest, setGlobalLoading]
  );

  const clearFilterCb = useCallback(
    (filterState, requestState) => {
      fetchMonthlySaleReports(filterState, requestState);
      setHasAppliedFilter(false);
    },
    [fetchMonthlySaleReports]
  );

  const applyFilterCb = useCallback(
    (searchKey) => {
      const { filterState, requestState } = modifyFilters({
        page: 1,
        searchKey,
      });
      fetchMonthlySaleReports(filterState, requestState);
      setHasAppliedFilter(true);
    },
    [fetchMonthlySaleReports, modifyFilters]
  );

  const sortCb = useCallback(
    ({ value, key }) => {
      const { filterState, requestState } = modifyFilters({ sort: { key, value } });
      fetchMonthlySaleReports(filterState, requestState);
    },
    [fetchMonthlySaleReports, modifyFilters]
  );

  const exportXlsx = useCallback(() => {
    setGlobalLoading(true);
    getMonthlySaleRequestExport(
      mapObject({ ...requestState }, monthlySaleReportExportRequest)
    );
    setGlobalLoading(false);
  }, [getMonthlySaleRequestExport, requestState, setGlobalLoading]);

  const noResult = useMemo(() => {
    return (
      <div className="pt-lg">
        <p className="text-xl text-gray-600">{lang.noReportsFound}</p>
        <p className="text-md text-gray-400">{lang.weDidNotFoundReports}</p>
      </div>
    );
  }, []);

  const saleResultContent = useMemo(() => {
     if (hasAppliedFilter) {
      return `${searchMonthlySaleReportResult.metadata.total} ${pluralize(
        searchMonthlySaleReportResult.metadata.total,
        lang.searchResult,
        lang.searchResults
      )}`;
    }
    return null;
  }, [searchMonthlySaleReportResult.metadata.total, hasAppliedFilter]);

  return (
    <ModuleWrapper>
      <HeaderA
        title={lang.monthlySaleReport}
        button={{
          iconPrefix: <Icon className="mr-sm" name="download" paddingless fontSize={12} />,
          text: lang.exportXlsx,
          loading: getMonthlySaleRequestExportLoading,
          disabled: getMonthlySaleRequestExportLoading,
          onClick: () => {
            exportXlsx();
          },
        }}
        className="mb-md"
      />

      <MonthlySaleReportFilter
        filterState={filterState}
        requestState={requestState}
        modifyFilter={modifyFilter}
        clearFilter={clearFilter}
        onClear={clearFilterCb}
        onApply={applyFilterCb}
      />

      {!loadingMonthlySaleReports ? (
        <DataTable
          resultContent={saleResultContent}
          page={filterState.page}
          pageSize={filterState.pageSize}
          onChangePage={modifyFilters}
          fetchList={fetchMonthlySaleReports}
          total={searchMonthlySaleReportResult?.metadata?.total || 0}
          loading={loadingMonthlySaleReports}
          columns={MonthlySaleReportColumns}
          data={searchMonthlySaleReportResult?.data || []}
          error={errorMonthlySaleReports}
          sort={filterState.sort}
          setSort={sortCb}
          hasAppliedFilter={isFilterDirty && hasAppliedFilter}
        />
      ) : (
        noResult
      )}
    </ModuleWrapper>
  );
};

export default MonthlySaleReport;
